<template>
    <div>
        <div class="md:flex md:items-center md:justify-between">
            <div class="min-w-0 flex-1">
                <router-link
                    :to="{ name: 'editVenue' }"
                    class="mb-2 flex inline-flex items-center text-sm text-gray-500"
                >
                    <base-back-icon />
                    <span>Settings</span>
                </router-link>
                <base-page-title title="Front Integration" />
            </div>

            <BaseSpinner :isLoading="isLoading" />
        </div>
        <div>
            <div class="mt-4 md:grid md:grid-cols-3 md:gap-6">
                <div class="md:col-span-1">
                    <div class="sm:pl-0 sm:pr-5">
                        <h3
                            class="text-base font-semibold leading-6 text-gray-800"
                        >
                            Details
                        </h3>
                        <p class="mt-2 text-sm text-gray-500">
                            Configure your Front live chat widget by providing your Front Chat ID.
                        </p>
                        <p class="mt-2 text-sm text-gray-500">
                            You'll need a Front account,
                            <a
                                class="text-indigo-600"
                                href="https://help.front.com/en/articles/2049"
                                rel="noopener"
                                target="_blank"
                                >and your chat ID</a
                            >.
                        </p>
                    </div>
                </div>
                <div class="mt-5 md:col-span-2 md:mt-0" v-if="!isLoading">
                    <div class="shadow sm:rounded-md">
                        <div class="space-y-6 bg-white px-4 py-5 sm:p-6">
                            <div class="grid grid-cols-3 gap-6">
                                <div class="sm:col-span-6">
                                    <label
                                        for="frontChatId"
                                        class="block text-sm font-medium leading-5 text-gray-700"
                                    >
                                        Front Chat ID
                                    </label>
                                    <input
                                        id="frontChatId"
                                        v-model="front.frontChatId"
                                        type="text"
                                        class="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                                        :class="{
                                            'border-red-300': $v.front.frontChatId.$error
                                        }"
                                        placeholder="Enter your Front Chat ID"
                                    />
                                    <p
                                        v-if="$v.front.frontChatId.$error"
                                        class="mt-2 text-sm text-red-600"
                                    >
                                        Front Chat ID is required
                                    </p>
                                </div>

                                <div class="sm:col-span-6">
                                    <label
                                        for="enabled"
                                        class="block text-sm font-medium leading-5 text-gray-700"
                                    >
                                        Integration Status
                                    </label>

                                    <div class="mt-2 flex items-start">
                                        <div class="w-full" v-auto-animate>
                                            <div
                                                class="flex flex-row items-center"
                                                @click="$v.$touch()"
                                            >
                                                <toggle-switch
                                                    v-model="front.enabled"
                                                    :isDisabled="
                                                        !hasValidConfig
                                                    "
                                                    @toggle-enable="
                                                        toggleEnabled
                                                    "
                                                />
                                                <label
                                                    class="ml-2 text-sm text-gray-500"
                                                >
                                                    {{
                                                        front.enabled
                                                            ? 'Enabled - Front live chat widget is active'
                                                            : 'Disabled - Front live chat widget is inactive'
                                                    }}
                                                </label>
                                            </div>
                                            <p
                                                v-if="!hasValidConfig"
                                                class="mt-1 text-sm text-red-600"
                                            >
                                                Please save the configuration
                                                first before enabling the
                                                integration.
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <BaseSpacerWithRuler />

        <div class="my-6 ml-4 flex flex-shrink-0 justify-end">
            <span
                class="inline-flex rounded-md shadow-sm"
                @click="saveSettings"
            >
                <button
                    type="button"
                    class="relative inline-flex items-center rounded-md border border-transparent bg-indigo-600 px-4 py-2 text-sm font-medium leading-5 text-white hover:bg-indigo-500 focus:border-indigo-700 focus:shadow-outline-indigo focus:outline-none active:bg-indigo-700"
                    :disabled="isLoading"
                >
                    Save
                </button>
            </span>
        </div>
    </div>
</template>

<script>
import { required } from 'vuelidate/lib/validators';
import ToggleSwitch from '@/components/formComponents/ToggleSwitch';

export default {
    name: 'FrontSettings',
    components: { ToggleSwitch },
    metaInfo: {
        title: 'Front Integration'
    },
    props: {
        venueId: {
            type: Number,
            required: true
        }
    },
    data() {
        return {
            isLoading: false,
            hasExistingConfig: false,
            front: {
                frontChatId: '',
                enabled: false
            }
        };
    },
    validations: {
        front: {
            frontChatId: {
                required
            }
        }
    },
    async created() {
        await this.fetchSettings();
    },
    computed: {
        hasValidConfig() {
            return this.hasExistingConfig;
        }
    },
    methods: {
        async fetchSettings() {
            try {
                this.isLoading = true;

                const { data } = await this.$axios.get(
                    `/venues/${this.venueId}/front/config`
                );

                if (data.success && data.frontConfig) {
                    this.hasExistingConfig = true;
                    this.front = {
                        frontChatId: data.frontConfig.frontChatId || '',
                        enabled: data.frontConfig.enabled || false
                    };
                } else {
                    this.hasExistingConfig = false;
                    this.front = {
                        frontChatId: '',
                        enabled: false
                    };
                }
            } catch (e) {
                if (e.response?.status === 404) {
                    this.hasExistingConfig = false;
                    this.front = {
                        frontChatId: '',
                        enabled: false
                    };
                } else {
                    console.error('Error fetching Front settings:', e);
                }
            } finally {
                this.isLoading = false;
            }
        },

        async toggleEnabled() {
            if (!this.hasValidConfig) {
                return;
            }

            try {
                this.front.enabled = !this.front.enabled;

                await this.$axios.post(
                    `/venues/${this.venueId}/front/toggle`,
                    {
                        enabled: this.front.enabled
                    }
                );

                this.$notify({
                    group: 'settings',
                    title: this.front.enabled
                        ? 'Integration enabled'
                        : 'Integration disabled',
                    type: 'success'
                });
            } catch (error) {
                this.front.enabled = !this.front.enabled;

                this.$notify({
                    group: 'settings',
                    title: 'Failed to update integration status',
                    text:
                        'Please try again or contact support if the problem persists',
                    type: 'error'
                });

                console.error('Front toggle error:', error);
            }
        },

        async saveSettings() {
            this.$v.$touch();

            if (this.$v.$invalid) {
                this.$notify({
                    group: 'settings',
                    title: 'Please fill in all required fields',
                    type: 'error'
                });
                return false;
            }

            try {
                this.isLoading = true;

                await this.$axios.post(
                    `/venues/${this.venueId}/front/config`,
                    {
                        enabled: this.front.enabled,
                        frontChatId: this.front.frontChatId
                    }
                );

                this.hasExistingConfig = true;

                this.$notify({
                    group: 'settings',
                    title: 'Settings saved successfully',
                    type: 'success'
                });
            } catch (error) {
                console.error('Front config save error:', error);

                if (error.response?.data?.message) {
                    this.$notify({
                        group: 'settings',
                        title: 'Failed to save settings',
                        text: error.response.data.message,
                        type: 'error'
                    });
                } else {
                    this.$notify({
                        group: 'settings',
                        title: 'Failed to save settings',
                        text: 'Please check your configuration and try again',
                        type: 'error'
                    });
                }

                this.front.enabled = false;
            } finally {
                this.isLoading = false;
            }
        }
    }
};
</script>
